import { Navigate, Outlet, RouteObject, useLocation, useRoutes } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Login from '@/pages/LoginPage'
import ForgotPassword from '@/pages/ForgotPasswordPage'
import ResetPassword from '@/pages/ResetPasswordPage'
import Dashboard from '@/pages/DashboardPage'
import Home from '@/pages/HomePage'
import BetaInterestPage from '@/pages/BetaInterestPage'
import BotSettings from '@/pages/BotSettings'
import AdvancedBotSettings from '@/pages/AdvancedBotSettings'
import ProductsPage from '@/pages/ProductsPage'
import Catalog from '@/pages/Catalog'
import PromotionsPage from '@/pages/PromotionsPage'
import PublicCatalogPage from '@/pages/PublicCatalogPage'
import InventorySyncPage from '@/pages/InventorySyncPage'
import OrderWebhooksPage from '@/pages/orderWebhooks/OrderWebhooksPage'
import NotFound from '@/pages/NotFound'
import { useAuthStore } from '@/store/authStore'
import InstancesList from '@/pages/whatsapp/InstancesList'
import ConversationsPage from '@/pages/conversations/ConversationsPage'
import AdminDashboard from '@/admin/pages/AdminDashboard'
import OrganizationSettings from '@/pages/OrganizationSettings'
import ProfilePage from '@/pages/ProfilePage'
import WebChatSetup from '@/pages/channels/WebChatSetup'
import OnboardingWizard from '@/pages/OnboardingWizard'
import TagsSettings from '@/pages/TagsSettings'
import BillingPage from '@/pages/BillingPage'
import CategoriesSettings from '@/pages/CategoriesSettings'
import FAQSettings from '@/pages/FAQSettings'
import EscalationSettings from '@/pages/EscalationSettings'
import PaymentSettings from '@/pages/PaymentSettings'
import CustomRulesSettings from '@/pages/CustomRulesSettings'
import TenantSynonymsWizardPage from '@/pages/TenantSynonymsWizardPage'
import ContactsPage from '@/pages/contacts/ContactsPage'
import CannedResponsesPage from '@/pages/CannedResponsesPage'
import OrdersPage from '@/pages/orders/OrdersPage'
import OrderReceiptPage from '@/pages/orders/OrderReceiptPage'
import AppointmentsPage from '@/pages/appointments/AppointmentsPage'
import TermsPage from '@/pages/legal/TermsPage'
import PrivacyPage from '@/pages/legal/PrivacyPage'
import DataDeletionPage from '@/pages/legal/DataDeletionPage'
import DebugAreaLayout from '@/admin/pages/DebugAreaLayout'
import { listChannelInstances } from '@/services/channelsApi'
import { isOnboardingCompleted, checkAndSyncOnboardingStatus } from '@/services/onboardingState'
import { VerticalUIProvider } from '@/lib/verticalUI'
import { TourAutoStart } from '@/components/TourAutoStart'

function PrivateGuard() {
  const token = useAuthStore((s) => s.token)
  if (!token) return <Navigate to="/login" replace />
  return (
    <VerticalUIProvider>
      <Outlet />
    </VerticalUIProvider>
  )
}

function OnboardingGuard() {
  const token = useAuthStore((s) => s.token)
  const tenantId = useAuthStore((s) => s.tenantId)
  const location = useLocation()
  const [checking, setChecking] = useState(true)
  const [mustRedirect, setMustRedirect] = useState(false)

  useEffect(() => {
    let mounted = true

    const check = async () => {
      if (!token || !tenantId) {
        if (!mounted) return
        setChecking(false)
        setMustRedirect(false)
        return
      }

      const completed = isOnboardingCompleted(tenantId)
      if (completed) {
        if (!mounted) return
        setChecking(false)
        setMustRedirect(false)
        return
      }

      try {
        const isServerCompleted = await checkAndSyncOnboardingStatus(tenantId)
        if (isServerCompleted) {
          if (!mounted) return
          setChecking(false)
          setMustRedirect(false)
          return
        }

        const instances = await listChannelInstances()
        if (!mounted) return
        setMustRedirect(instances.length === 0)
      } catch {
        if (!mounted) return
        setMustRedirect(false)
      } finally {
        if (mounted) setChecking(false)
      }
    }

    check()
    return () => {
      mounted = false
    }
  }, [token, tenantId, location.pathname])

  if (checking) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    )
  }

  if (
    mustRedirect &&
    !location.pathname.startsWith('/app/onboarding') &&
    !location.pathname.startsWith('/app/instances')
  ) {
    return <Navigate to="/app/onboarding" replace />
  }

  return (
    <>
      {!location.pathname.startsWith('/app/onboarding') && <TourAutoStart />}
      <Outlet />
    </>
  )
}

function PublicGuard() {
  const token = useAuthStore((s) => s.token)
  if (token) return <Navigate to="/app/dashboard" replace />
  return <Outlet />
}

export default function AppRoutes() {
  const routes: RouteObject[] = [
    // Formulario de interés en la fase beta (público, también para sesiones iniciadas)
    { path: '/reserva-tu-lugar', element: <BetaInterestPage /> },

    // Legal & Meta Compliance pages (Publicly accessible)
    { path: '/terms', element: <TermsPage /> },
    { path: '/terminos', element: <TermsPage /> },
    { path: '/terminos-y-condiciones', element: <TermsPage /> },
    { path: '/terms-and-conditions', element: <TermsPage /> },
    { path: '/privacy', element: <PrivacyPage /> },
    { path: '/privacidad', element: <PrivacyPage /> },
    { path: '/privacity', element: <PrivacyPage /> },
    { path: '/politica-privacidad', element: <PrivacyPage /> },
    { path: '/privacy-policy', element: <PrivacyPage /> },
    { path: '/data-deletion', element: <DataDeletionPage /> },
    { path: '/eliminacion-datos', element: <DataDeletionPage /> },
    { path: '/data-deletion-instructions', element: <DataDeletionPage /> },

    // In-app legal aliases (accessible without authentication)
    { path: '/app/terms', element: <TermsPage /> },
    { path: '/app/terminos', element: <TermsPage /> },
    { path: '/app/privacy', element: <PrivacyPage /> },
    { path: '/app/privacidad', element: <PrivacyPage /> },
    { path: '/app/privacity', element: <PrivacyPage /> },
    { path: '/app/data-deletion', element: <DataDeletionPage /> },
    { path: '/app/eliminacion-datos', element: <DataDeletionPage /> },

    { path: '/:tenantSlug/catalog', element: <PublicCatalogPage /> },
    { path: '/:tenantSlug/catalog/:productSlug', element: <PublicCatalogPage /> },
    {
      element: <PublicGuard />,
      children: [
        { path: '/', element: <Home /> },
        { path: '/login', element: <Login /> },
        { path: '/register', element: <Login initialMode="signup" /> },
        { path: '/registro', element: <Login initialMode="signup" /> },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
      ],
    },
    {
      path: '/app',
      element: <PrivateGuard />,
      children: [
        {
          element: <OnboardingGuard />,
          children: [
            { path: 'onboarding', element: <OnboardingWizard /> },
            { path: 'dashboard', element: <Dashboard /> },
            { path: 'instances', element: <InstancesList /> },
            { path: 'conversations', element: <ConversationsPage /> },
            { path: 'conversations/:id', element: <ConversationsPage /> },
            { path: 'bot-settings', element: <BotSettings /> },
            { path: 'bot-settings/advanced', element: <AdvancedBotSettings /> },
            { path: 'bot-settings/faq', element: <FAQSettings /> },
            { path: 'bot-settings/payments', element: <PaymentSettings /> },
            { path: 'bot-settings/escalation', element: <EscalationSettings /> },
            { path: 'bot-settings/rules', element: <CustomRulesSettings /> },
            { path: 'products', element: <ProductsPage /> },
            { path: 'catalog', element: <Catalog /> },
            { path: 'inventory-sync', element: <InventorySyncPage /> },
            { path: 'order-webhooks', element: <OrderWebhooksPage /> },
            { path: 'catalog-intelligence', element: <Navigate to="/app/products" replace /> },
            { path: 'categories', element: <CategoriesSettings /> },
            { path: 'promotions', element: <PromotionsPage /> },
            { path: 'organization', element: <OrganizationSettings /> },
            { path: 'profile', element: <ProfilePage /> },
            { path: 'channels/webchat/:id/setup', element: <WebChatSetup /> },
            { path: 'tags', element: <TagsSettings /> },
            { path: 'synonyms', element: <TenantSynonymsWizardPage /> },
            { path: 'billing', element: <BillingPage /> },
            { path: 'contacts', element: <ContactsPage /> },
            { path: 'canned-responses', element: <CannedResponsesPage /> },
            { path: 'orders', element: <OrdersPage /> },
            { path: 'orders/:id/receipt', element: <OrderReceiptPage /> },
            { path: 'orders/:id', element: <OrdersPage /> },
            { path: 'appointments', element: <AppointmentsPage /> },
            { path: '', element: <Navigate to="dashboard" replace /> },
          ],
        },
      ],
    },
    { path: '/admin/debug/*', element: <DebugAreaLayout /> },
    { path: '/admin/*', element: <AdminDashboard /> },
    { path: '*', element: <NotFound /> },
  ]
  return useRoutes(routes)
}
